import Vue from "vue";
import VueRouter from "vue-router";
import LayoutAuthBlank from "@/components/layouts/LayoutAuthBlank.vue";
import LayoutConsoleV2 from "@/components/layouts/LayoutConsoleV2.vue";
import LayoutDeveloperConsole from "@/components/layouts/LayoutDeveloperConsole.vue";
import routerSSO from "@/services/sso/router/index";
import routerHrdc from "@/services/hrdc/routes/index";
import store from "@/store/index";
import multiguard from "vue-router-multiguard";

const limitToGuest = function (to, from, next) {
  if (store.state.auth.data.token) next({ name: "PageConsoleDashboard" });
  next();
};
const limitToUser = function (to, from, next) {
  if (!store.state.auth.data.token) next({ name: "PageGuestHome" });
  next();
};
// Developer console — a completely separate auth track. Page access is governed
// solely by a dev token obtained with the secret key; a normal user session
// (store.state.auth) grants nothing here.
const limitToDeveloper = function (to, from, next) {
  if (!store.state.devAuth.data || !store.state.devAuth.data.token) {
    return next({ name: "PageDeveloperLogin" });
  }
  next();
};
const limitToDeveloperGuest = function (to, from, next) {
  if (store.state.devAuth.data && store.state.devAuth.data.token) {
    return next({ name: "PageDeveloperSettings" });
  }
  next();
};

Vue.use(VueRouter);

//BOC:[locale]
import configLocale from "@/config/locale";
import _ from "lodash";
var pathArray = window.location.pathname.split("/");
var locale = pathArray[1];
var path = pathArray[2];
if (locale && _.findIndex(configLocale, { value: locale }) == -1) {
  //fallback to first available locale - preserve full path
  var fullPath = window.location.pathname;
  var search = window.location.search || "";
  window.location.href = `${window.location.origin}/${configLocale[0].value}${fullPath}${search}`;
} else if (locale && (path == null || path == "")) {
  window.location.href = `${window.location.origin}/${locale}/home`;
}
//EOC

const routes = [
  {
    path: "/",
    redirect: { name: "PageGuestHome" },
  },
  {
    path: "/",
    component: LayoutAuthBlank,
    beforeEnter: multiguard([limitToGuest]),
    children: [
      {
        path: "home",
        name: "PageGuestHome",
        component: () => import("@/views/guest/PageGuestHome.vue"),
        props: true,
      },
      {
        path: "login/firstTime",
        name: "PageGuestLoginFirstTime",
        component: () => import("@/views/guest/PageGuestLoginFirstTime.vue"),
        props: true,
      },
      {
        path: "login/firstTime/setPassword",
        name: "PageGuestLoginFirstTimeSetPassword",
        component: () =>
          import("@/views/guest/PageGuestLoginFirstTimeSetPassword.vue"),
        props: true,
      },

      {
        path: "login/resetPassword",
        name: "PageResetPassword",
        component: () => import("@/views/guest/PageResetPassword.vue"),
        props: true,
      },

      {
        path: "login/resetPassword/confirm",
        name: "PagePasswordConfirmation",
        component: () => import("@/views/guest/PagePasswordConfirmation.vue"),
        props: (route) => ({
          params: {
            token: route.query.token,
          },
        }),
      },
    ],
  },
  {
    path: "/",
    component: LayoutConsoleV2,
    beforeEnter: multiguard([limitToUser]),
    children: [
      routerHrdc,
      routerSSO,
      {
        path: "service",
        name: "PageServiceBrowse",
        redirect: { name: "PageConsoleDashboard" },
      },
      {
        path: "service/:serviceKey",
        name: "PageServiceLanding",
        redirect: { path: "service/:serviceKey/dashboard" },
      },
      {
        path: "service/:serviceKey/redirect",
        name: "PageServiceRedirect",
        redirect: { path: "service/:serviceKey/dashboard" },
      },
      {
        path: "service/:serviceKey/dashboard",
        name: "PageServiceDashboard",
        component: () => import("@/views/service/PageServiceDashboard.vue"),
      },
      {
        path: "service/:serviceKey/model/:modelKey",
        name: "PageServiceModelBrowse",
        // v-data-table listing (BreadDataTable). The Tabulator page it replaced
        // is still wired to the `dev-model` routes below.
        component: () =>
          import("@/views/service/DataTablePages/PageServiceModelBrowse.vue"),
      },
      // routes for tabulator pages
      {
        path: "service/:serviceKey/dev-model/:modelKey",
        name: "PageServiceModelBreadTabulatorBrowse",
        component: () =>
          import("@/views/service/TabulatorPages/PageServiceModelBrowse.vue"),
      },
      {
        path: "service/:serviceKey/dev-model/:modelKey/:modelId/:childModelKey",
        name: "PageServiceModelBreadTabulatorBrowseParent",
        component: () =>
          import(
            "@/views/service/TabulatorPages/PageServiceModelBrowseParent.vue"
          ),
      },
      {
        path: "service/:serviceKey/dev-model/:modelKey/new",
        name: "PageServiceModelBreadTabulatorAdd",
        component: () =>
          import("@/views/service/TabulatorPages/PageServiceModelAdd.vue"),
      },
      {
        path: "service/:serviceKey/dev-model/:modelKey/:modelId",
        name: "PageServiceModelBreadTabulatorRead",
        // Migrated to v-data-table (BreadDataTable) — the route name is kept so
        // the ~15 call sites that push to it don't have to change.
        component: () =>
          import("@/views/service/DataTablePages/PageServiceModelRead.vue"),
      },
      {
        path: "service/:serviceKey/dev-model/:modelKey/:modelId/item/edit",
        name: "PageServiceModelBreadTabulatorEdit",
        // Redesigned form page; see the note on the read route above.
        component: () =>
          import("@/views/service/DataTablePages/PageServiceModelEdit.vue"),
      },
      {
        path: "service/:serviceKey/dev-model/:modelKey/:modelId/item/delete",
        name: "PageServiceModelBreadTabulatorDelete",
        component: () =>
          import("@/views/service/TabulatorPages/PageServiceModelDelete.vue"),
      },
      {
        path: "service/:serviceKey/dev-model/:parentModelKey/:parentModelId/:childModelKey/new",
        name: "PageServiceModelBreadTabulatorChildAdd",
        component: () =>
          import("@/views/service/TabulatorPages/PageServiceModelChildAdd.vue"),
      },
      {
        path: "service/:serviceKey/dev-model/:modelKey/:modelId/deleteHard",
        name: "PageServiceModelBreadTabulatorDeleteHard",
        component: () =>
          import(
            "@/views/service/TabulatorPages/PageServiceModelDeleteHard.vue"
          ),
      },
      {
        path: "service/:serviceKey/dev-model/:modelKey/:modelId/restore",
        name: "PageServiceModelBreadTabulatorRestore",
        component: () =>
          import("@/views/service/TabulatorPages/PageServiceModelRestore.vue"),
      },
      {
        path: "service/:serviceKey/dev-model/:parentModelKey/:parentModelId/:intermediateModelKey/:intermediateModelId/:childModelKey/detach",
        name: "PageServiceModelBreadTabulatorChildDetach",
        component: () =>
          import(
            "@/views/service/TabulatorPages/PageServiceModelChildDetach.vue"
          ),
      },
    ],
  },
  //BOC:[developer console — secret-key auth, separate from normal login]
  {
    // Login: blank layout, only reachable when NOT already a dev.
    path: "/",
    component: LayoutAuthBlank,
    beforeEnter: multiguard([limitToDeveloperGuest]),
    children: [
      {
        path: "developer/login",
        name: "PageDeveloperLogin",
        component: () => import("@/views/developer/PageDeveloperLogin.vue"),
        meta: { title: "Developer Login" },
      },
    ],
  },
  {
    // Console: dedicated dev layout, requires a dev token. Every child carries
    // meta.requiresDev so api.js attaches the dev token (not the user token).
    path: "/",
    component: LayoutDeveloperConsole,
    beforeEnter: multiguard([limitToDeveloper]),
    children: [
      {
        path: "developer",
        name: "PageDeveloperSettings",
        component: () => import("@/views/developer/PageDeveloperSettings.vue"),
        meta: { title: "Developer Settings", breadcrumb: ["Settings"], requiresDev: true },
      },
      {
        path: "developer/hrdc/job",
        name: "DeveloperHrdcJob",
        component: () => import("@/views/developer/modules/HrdcJob.vue"),
        props: { params: { modelKey: "HrdcSettingsJob" } },
        meta: { title: "Developer · HRDC Job", breadcrumb: ["HRDC Job"], requiresDev: true },
      },
      {
        path: "developer/hrdc/xero-token",
        name: "DeveloperHrdcXeroToken",
        component: () => import("@/views/developer/modules/HrdcXeroToken.vue"),
        props: { params: { modelKey: "HrdcSettingsXero" } },
        meta: { title: "Developer · HRDC XERO Token", breadcrumb: ["HRDC XERO Token"], requiresDev: true },
      },
      {
        path: "developer/hrdc-event/xero-token",
        name: "DeveloperHrdcEventXeroToken",
        component: () =>
          import("@/views/developer/modules/HrdcEventXeroToken.vue"),
        props: { params: { modelKey: "HrdcEventSettingsXero" } },
        meta: { title: "Developer · HRDC Event XERO Token", breadcrumb: ["HRDC Event XERO Token"], requiresDev: true },
      },
      {
        // Xero OAuth popup callback for the dev console (uses the dev identity).
        // Service to store against rides in the OAuth `state` param.
        path: "developer/callback_xero",
        name: "DeveloperXeroCallback",
        component: () => import("@/views/developer/modules/XeroCallback.vue"),
        meta: { title: "Developer · XERO Callback", breadcrumb: ["XERO Callback"], requiresDev: true },
      },
    ],
  },
  //EOC
  {
    path: "/console",
    component: LayoutConsoleV2,
    beforeEnter: multiguard([limitToUser]),
    children: [
      {
        path: "",
        redirect: { name: "PageConsoleDashboard" },
      },
      {
        path: "dashboard",
        name: "PageConsoleDashboard",
        component: () => import("@/views/console/PageConsoleDashboard.vue"),
        meta: {
          title: "Dashboard",
        },
      },
      {
        path: "profile",
        name: "PageConsoleProfile",
        component: () => import("@/views/console/PageConsoleProfile.vue"),
        meta: {
          title: "My Profile",
        },
      },
      {
        path: "logout",
        name: "PageConsoleLogout",
        component: () => import("@/views/console/PageConsoleLogout.vue"),
      },
    ],
  },
];

const router = new VueRouter({
  base: locale,
  mode: "history",
  routes,
});

//BOC:[developer token scope] keep devAuth.active in sync with the current area
router.afterEach((to) => {
  store.commit("setDevActive", !!(to.meta && to.meta.requiresDev));
});
//EOC
//BOC:[page title]
router.afterEach((to) => {
  Vue.nextTick(() => {
    document.title = to.meta.title
      ? to.meta.title + " | YYC taxPOD HRDC"
      : "YYC taxPOD HRDC";
  });
});
//EOC

export default router;
